// ===========================================================================
// Pre-Visit Planning Service (Módulo D — BillingV2)
//
// TODO BACKEND: todos los métodos devuelven datos fake (services/fakeData.js)
// con latencia simulada. Cuando el schema `prevvisit` exista, reemplazar por
// llamadas axios reales (src/utils/Apis/MCMCore/axios.js) a /api/previsit/*.
// ===========================================================================

import { FAKE_APPOINTMENTS } from "./fakeData";
import { PREP_STATUS } from "../common/constants";

const SIMULATED_LATENCY_MS = 350;
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Obtiene las citas del día seleccionado con su prep packet (huddle sheet).
 *
 * Endpoint futuro:
 * GET /api/previsit/huddles?date=YYYY-MM-DD&locationId={id}
 *
 * Respuesta esperada (200): array de huddle sheets por cita, consolidando
 * HCCs pendientes (Coding Opportunities), care gaps abiertos, meds y labs
 * (tablas prevvisit.TAB_HUDDLE_SHEET + TAB_HUDDLE_ITEM).
 *
 * @param {"today"|"tomorrow"} dateKey - día a consultar
 * @returns {Promise<Object[]>} citas con prep packet
 */
export const getAppointments = async (dateKey) => {
  await delay(SIMULATED_LATENCY_MS);
  // Copia defensiva: la página muta estado local (optimistic UI).
  return FAKE_APPOINTMENTS.filter((a) => a.dateKey === dateKey).map((a) => ({ ...a }));
};

/**
 * Marca el prep packet como revisado por el provider.
 *
 * Endpoint futuro:
 * PUT /api/previsit/huddles/{appointmentId}/review
 * Body: { reviewedBy: number }
 *
 * Respuesta esperada (200): { "result": 0, "message": "Huddle reviewed" }
 * Posibles errores (400): { "result": 1, "message": "Huddle not found" }
 *
 * @param {string} appointmentId
 * @returns {Promise<{result: number, status: string}>}
 */
export const markPrepReviewed = async (appointmentId) => {
  await delay(200);
  // Local-only: el backend no existe aún; la página aplica el cambio optimista.
  return { result: 0, status: PREP_STATUS.REVIEWED, appointmentId };
};

/**
 * Calcula las métricas de la jornada para la Signal Bar.
 * (En backend será un agregado del SP que arma los huddles.)
 *
 * Endpoint futuro:
 * GET /api/previsit/huddles/summary?date=YYYY-MM-DD
 *
 * @param {Object[]} appointments - citas ya cargadas
 * @returns {{appointments: number, prepReady: number, suspectedHcc: number, openGaps: number, medsToReconcile: number}}
 */
export const buildDaySignals = (appointments = []) => ({
  appointments: appointments.length,
  prepReady: appointments.filter(
    (a) => a.status === PREP_STATUS.READY || a.status === PREP_STATUS.REVIEWED
  ).length,
  suspectedHcc: appointments.reduce((acc, a) => acc + a.suspectedHcc.length, 0),
  openGaps: appointments.reduce((acc, a) => acc + a.careGaps.length, 0),
  medsToReconcile: appointments.reduce((acc, a) => acc + a.medsToReconcile.length, 0),
});
